/**
 * moonshot-collection.js
 *
 * Collection grid behavior:
 *   - Populates the per-tile chip (moonshot-grid-tile-chip.liquid) with
 *     the right label based on home-store stock.
 *   - Respects plan §14.4.1: batches requests, lazy-loads beyond the
 *     first 12 tiles, shares the 60s cache with moonshot-stock-widget.js.
 *
 * Runs on collection/search pages. No-op elsewhere. Safe to include in
 * every layout.
 */

/* deploy: 2026-10-05 reorder-fix v2 */
(function () {
  "use strict";
  const M = window.Moonshot;
  if (!M) return;

  const BATCH_SIZE = 12;

  // Home-first auto-sort toggle. DISABLED 2026-10-05.
  // Reordering tiles (parent.appendChild) every time a tile's availability
  // resolved — on the initial eager batch AND lazily as tiles scrolled into
  // view — reshuffled the grid under the customer's cursor, making products
  // "jump" on load and while scrolling and rendering Add to Cart nearly
  // unclickable. Worst on large, mostly-out-of-stock collections where tiles
  // resolve via the Storefront API on scroll (e.g. the 956-product Reality
  // Fracture Singles). Tiles now keep the merchant's collection order; the
  // availability chips still populate in place (their space is reserved in CSS,
  // so filling them causes no layout shift). Re-introduce home-first ordering
  // only in a non-disruptive way (server-side / before first paint), never by
  // reordering already-painted tiles. The sort code below is kept, gated off.
  const ENABLE_HOME_FIRST_SORT = false;
  // Storefront API exposes per-location availability via storeAvailability,
  // NOT via inventoryItem.inventoryLevels (that path is Admin-API-only). Same
  // correction we applied to the PDP stock widget in Phase 1. Requires the
  // `unauthenticated_read_product_pickup_locations` scope on the storefront
  // access token. Token must be minted from the same app that wrote the
  // location metafields (Moonshot Admin Scripts) — otherwise `$app:` resolves
  // to the calling app and metafields come back empty.
  const INVENTORY_QUERY = /* GraphQL */ `
    query TileStock($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          quantityAvailable
          storeAvailability(first: 20) {
            edges {
              node {
                available
                quantityAvailable
                location {
                  metafields(identifiers: [
                    { namespace: "$app:multilocation", key: "storefront_group" },
                    { namespace: "$app:multilocation", key: "is_customer_visible" }
                  ]) { key value }
                }
              }
            }
          }
        }
      }
    }
  `;

  // Share the cache exported by moonshot-stock-widget.js if present,
  // otherwise maintain a small local cache.
  const cache = window.MoonshotStockCache || new Map();
  window.MoonshotStockCache = cache;
  const CACHE_TTL_MS = 60 * 1000;

  function toGid(id) {
    if (!id) return null;
    const s = String(id);
    return s.indexOf("gid://") === 0 ? s : "gid://shopify/ProductVariant/" + s;
  }

  function bucketFromNode(node) {
    /** @type {{[k:string]: number}} */
    const by = {};
    const edges =
      node && node.storeAvailability && node.storeAvailability.edges;
    const fallback = {
      availableForSale: !!(node && node.availableForSale),
      quantityAvailable:
        node && typeof node.quantityAvailable === "number" ? node.quantityAvailable : 0,
    };
    if (!edges) return { byGroup: by, fallback };
    for (const edge of edges) {
      const lvl = edge.node;
      if (!lvl) continue;
      const mfs = {};
      (lvl.location && lvl.location.metafields || []).forEach((m) => {
        if (m) mfs[m.key] = m.value;
      });
      if (mfs.is_customer_visible !== "true") continue;
      const group = mfs.storefront_group;
      if (!group) continue;
      const qty = typeof lvl.quantityAvailable === "number" ? lvl.quantityAvailable : 0;
      by[group] = (by[group] || 0) + qty;
    }
    return { byGroup: by, fallback };
  }

  // Per-customer purchase cap is 8 — showing larger numbers on tiles
  // would be misleading ("150 in stock" when you can only buy 8 looks
  // bad). Cap the DISPLAY at 8. Raw qty values still flow to the
  // sort/filter logic via homeQty/anyQty so ordering stays correct
  // (an item with 200 sells out later than one with 9).
  const DISPLAY_CAP = 8;
  function displayQty(n) {
    const x = typeof n === "number" ? n : 0;
    return x > DISPLAY_CAP ? DISPLAY_CAP : x;
  }

  /**
   * Returns the chip's display state given per-group stock + home store.
   *   text       primary line (e.g., "At Noblesville · 8")
   *   subText    optional secondary line (e.g., "Also at Plainfield · 4")
   *   klass      CSS modifier
   *   homeQty    qty at home store (used by filter/sort to know in-stock-here)
   *   anyQty     total qty across customer-visible groups (used by sort)
   *   atHome     boolean — for filter
   */
  function chipState(byGroup, homeGroup, isStreamOnly) {
    if (isStreamOnly) {
      // Amanda's note 2026-05-21: prior copy "Stream pickup at
      // Noblesville" misread as pickup-only. Stream items can SHIP or
      // be picked up at Noblesville (next day after the stream).
      // Surface both options so customers don't bounce thinking they
      // can't get it shipped.
      return {
        text: "Ships or pickup at " + M.labelFor("noblesville"),
        subText: "Stream item",
        klass: "moonshot-tile-chip--stream",
        homeQty: 0,
        anyQty: 1, // treat as in-stock-somewhere for sort purposes
        atHome: homeGroup === "noblesville",
      };
    }
    const home = byGroup[homeGroup] || 0;
    const otherGroup = homeGroup === "noblesville" ? "plainfield" : "noblesville";
    const other = byGroup[otherGroup] || 0;
    const anyQty = home + other;

    if (home > 0) {
      return {
        text: "At " + M.labelFor(homeGroup) + " · " + displayQty(home),
        subText: other > 0 ? "Also at " + M.labelFor(otherGroup) + " · " + displayQty(other) : "",
        klass: "moonshot-tile-chip--in",
        homeQty: home,
        anyQty,
        atHome: true,
      };
    }
    if (other > 0) {
      // Drop "Only" framing — items here ship from the other store
      // fine, so saying "Only" misleads the customer into thinking
      // they can't have it. The amber color carries the
      // not-at-your-store signal.
      return {
        text: "At " + M.labelFor(otherGroup) + " · " + displayQty(other),
        subText: "",
        klass: "moonshot-tile-chip--other",
        homeQty: 0,
        anyQty,
        atHome: false,
      };
    }
    return {
      text: "Out of stock",
      subText: "",
      klass: "moonshot-tile-chip--out",
      homeQty: 0,
      anyQty: 0,
      atHome: false,
    };
  }

  // Backward-compatible export for any external caller.
  function chipLabel(byGroup, homeGroup, isStreamOnly) {
    const s = chipState(byGroup, homeGroup, isStreamOnly);
    return { text: s.text, klass: s.klass };
  }

  function renderChip(tile, bucket) {
    const home = M.getHomeStore();
    const isStreamOnly = tile.getAttribute("data-is-stream-only") === "true";

    // bucket can be either { byGroup, fallback } (new shape) or a bare
    // byGroup map (legacy shape from older cache entries / external callers).
    const isNewShape = bucket && typeof bucket === "object" && "byGroup" in bucket;
    let byGroup = isNewShape ? bucket.byGroup || {} : bucket || {};
    const fallback = isNewShape ? bucket.fallback : null;

    // Non-shippable variants (e.g., events) return empty
    // storeAvailability.edges from the Storefront API even when
    // inventory exists at a pickup location. If so, fall back to the
    // variant-level quantityAvailable + the tile's data-venue tag to
    // attribute the stock to the right store.
    //
    // Cache may have been populated by either the chip (fallback.*) or
    // the PDP widget (bucket.variantQty) — accept either.
    const hasByGroupStock = byGroup.noblesville > 0 || byGroup.plainfield > 0;
    const fallbackQty =
      (fallback && fallback.availableForSale && fallback.quantityAvailable) ||
      (typeof bucket.variantQty === "number" ? bucket.variantQty : 0);
    if (!hasByGroupStock && fallbackQty > 0) {
      const venue = tile.getAttribute("data-venue");
      if (venue === "noblesville" || venue === "plainfield") {
        byGroup = Object.assign({}, byGroup, { [venue]: fallbackQty });
      }
    }

    const state = chipState(byGroup, home, isStreamOnly);

    const label = tile.querySelector(".moonshot-tile-chip__label");
    if (label) label.textContent = state.text;

    let sub = tile.querySelector(".moonshot-tile-chip__sub");
    if (state.subText) {
      if (!sub) {
        sub = document.createElement("span");
        sub.className = "moonshot-tile-chip__sub";
        tile.appendChild(sub);
      }
      sub.textContent = state.subText;
    } else if (sub) {
      sub.remove();
    }

    tile.classList.remove(
      "moonshot-tile-chip--in",
      "moonshot-tile-chip--other",
      "moonshot-tile-chip--out",
      "moonshot-tile-chip--stream",
    );
    tile.classList.add(state.klass);

    // Stash filter/sort signals on the tile itself so the filter+sort
    // logic can reorder/hide tiles without re-running the resolver.
    tile.dataset.moonshotHomeQty = String(state.homeQty);
    tile.dataset.moonshotAnyQty = String(state.anyQty);
    tile.dataset.moonshotAtHome = state.atHome ? "true" : "false";

    // Bubble an event so the filter+sort layer can react to newly-resolved tiles
    // (e.g., when scroll triggers a lazy batch).
    tile.dispatchEvent(
      new CustomEvent("moonshot:tile:resolved", {
        bubbles: true,
        detail: { homeQty: state.homeQty, anyQty: state.anyQty, atHome: state.atHome },
      }),
    );

    tile.hidden = false;
  }

  async function fetchBatch(tiles) {
    // Tiles carrying server-embedded admin-truth (the custom.online_availability
    // metafield) render directly — no fetch. That data is correct (counts only
    // online-fulfilling stock, excludes hidden backstock like the Vault and the
    // Warehouse), unlike storeAvailability which is pickup-only. Only tiles WITHOUT it fall through to the Storefront
    // API path (e.g. main-only singles that don't carry the metafield).
    const fetchTiles = [];
    for (const t of tiles) {
      const raw = t.getAttribute("data-online-availability");
      if (raw) {
        try {
          const av = JSON.parse(raw);
          renderChip(t, {
            byGroup: av.groups || {},
            fallback: {
              availableForSale: (av.fulfillable || 0) > 0,
              quantityAvailable: av.fulfillable || 0,
            },
          });
          continue;
        } catch (e) {
          /* malformed — fall through to fetch */
        }
      }
      fetchTiles.push(t);
    }
    if (fetchTiles.length === 0) return;

    const idToGid = new Map();
    for (const t of fetchTiles) {
      const id = t.getAttribute("data-variant-id");
      if (!id) continue;
      idToGid.set(t, toGid(id));
    }
    // Skip already-cached
    const now = Date.now();
    const toFetch = [];
    const cachedResults = new Map();
    for (const [t, gid] of idToGid) {
      const c = cache.get(gid);
      if (c && now - c.at < CACHE_TTL_MS) {
        cachedResults.set(t, c.data);
      } else {
        toFetch.push(gid);
      }
    }

    // Render whatever we had cached immediately
    for (const [t, data] of cachedResults) renderChip(t, data);

    if (toFetch.length === 0) return;

    try {
      const data = await M.storefront(INVENTORY_QUERY, { ids: toFetch });
      const gidToBucket = new Map();
      (data.nodes || []).forEach((node) => {
        if (!node || !node.id) return;
        const bucket = bucketFromNode(node);
        gidToBucket.set(node.id, bucket);
        cache.set(node.id, { at: now, data: bucket });
      });
      for (const [t, gid] of idToGid) {
        if (cachedResults.has(t)) continue;
        renderChip(t, gidToBucket.get(gid) || { byGroup: {}, fallback: null });
      }
    } catch (err) {
      // Fail silently — grid tiles are best-effort; PDPs remain authoritative.
      if (window.console && console.warn)
        console.warn("[Moonshot] tile batch fetch failed:", err.message || err);
    }
  }

  function processTiles(root) {
    const all = Array.from((root || document).querySelectorAll("[data-moonshot-tile]"));
    if (all.length === 0) return;

    // First BATCH_SIZE get fetched immediately; the rest on scroll via IntersectionObserver.
    const eager = all.slice(0, BATCH_SIZE);
    const lazy = all.slice(BATCH_SIZE);
    fetchBatch(eager);

    if (lazy.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const hits = entries.filter((e) => e.isIntersecting).map((e) => e.target);
        if (hits.length) {
          hits.forEach((t) => io.unobserve(t));
          fetchBatch(hits);
        }
      },
      { rootMargin: "200px" },
    );
    lazy.forEach((t) => io.observe(t));
  }

  // ─────────────────────────── auto-sort ──────────────────────────────
  // Silent default: home-store-stocked tiles bubble to the top, ties
  // broken by total stock desc. No UI — customers don't have to know
  // it's happening. We step back when the customer has explicitly
  // chosen a sort via Shopify's native dropdown (URL has ?sort_by=…),
  // because their explicit choice should win over our default
  // ordering.

  function shopifySortIsExplicit() {
    try {
      const url = new URL(window.location.href);
      // sort_by is only present in the URL when the customer changes
      // away from the merchant's collection default. If absent, we're
      // on the default and free to apply home-first.
      return url.searchParams.has("sort_by");
    } catch (e) {
      return false;
    }
  }

  function tileCardForSort(tile) {
    if (tile._moonshotCard) return tile._moonshotCard;
    let n = tile;
    while (n && n !== document.body) {
      if (n.classList && n.classList.contains("product-item")) break;
      n = n.parentElement;
    }
    if (!n || n === document.body) {
      n = tile.closest("li") || tile.closest("[class*=grid-item]") || tile.parentElement;
    }
    tile._moonshotCard = n || tile;
    return tile._moonshotCard;
  }

  function tileSortSignals(tile) {
    const homeQty = parseInt(tile.dataset.moonshotHomeQty || "0", 10) || 0;
    const anyQty = parseInt(tile.dataset.moonshotAnyQty || "0", 10) || 0;
    const atHome = tile.dataset.moonshotAtHome === "true";
    const resolved = tile.classList.contains("moonshot-tile-chip--in") ||
      tile.classList.contains("moonshot-tile-chip--other") ||
      tile.classList.contains("moonshot-tile-chip--out") ||
      tile.classList.contains("moonshot-tile-chip--stream");
    return { homeQty, anyQty, atHome, resolved };
  }

  function applyHomeFirstSort() {
    if (shopifySortIsExplicit()) return; // respect explicit customer sort

    const tiles = Array.from(document.querySelectorAll("[data-moonshot-tile]"));
    if (tiles.length === 0) return;

    const cards = tiles.map(tileCardForSort).filter(Boolean);
    // Stamp original DOM index once so unresolved tiles can be ordered
    // by their server-given position (preventing pop-around as lazy
    // batches resolve).
    cards.forEach((c, i) => {
      if (c.dataset.moonshotOrigIndex == null) c.dataset.moonshotOrigIndex = String(i);
    });

    const sigByCard = new Map();
    for (const t of tiles) sigByCard.set(tileCardForSort(t), tileSortSignals(t));

    const groups = new Map(); // parent → cards
    for (const c of cards) {
      if (!groups.has(c.parentElement)) groups.set(c.parentElement, []);
      groups.get(c.parentElement).push(c);
    }

    for (const [parent, list] of groups) {
      list.sort((a, b) => {
        const sa = sigByCard.get(a) || {};
        const sb = sigByCard.get(b) || {};
        // Unresolved tiles keep their original position to avoid
        // visible re-ordering as the Storefront API trickles in.
        if (!sa.resolved || !sb.resolved) {
          const ai = parseInt(a.dataset.moonshotOrigIndex || "0", 10);
          const bi = parseInt(b.dataset.moonshotOrigIndex || "0", 10);
          return ai - bi;
        }
        // atHome=true wins; ties broken by total qty desc; final tie
        // broken by orig index for stability.
        if (sa.atHome !== sb.atHome) return sa.atHome ? -1 : 1;
        if (sb.anyQty !== sa.anyQty) return sb.anyQty - sa.anyQty;
        const ai = parseInt(a.dataset.moonshotOrigIndex || "0", 10);
        const bi = parseInt(b.dataset.moonshotOrigIndex || "0", 10);
        return ai - bi;
      });
      for (const c of list) parent.appendChild(c);
    }
  }

  // Re-run sort when tiles resolve (lazy batches arrive) and when the
  // home store changes (atHome flag flips for every tile). Coalesce
  // burst calls into a single rAF tick — a 12-tile eager batch fires
  // 12 resolved events and we don't want to sort 12 times.
  let _sortPending = false;
  function scheduleSort() {
    if (_sortPending) return;
    _sortPending = true;
    requestAnimationFrame(() => {
      _sortPending = false;
      applyHomeFirstSort();
    });
  }
  if (ENABLE_HOME_FIRST_SORT) {
    document.addEventListener("moonshot:tile:resolved", scheduleSort);
  }

  function init() {
    processTiles(document);
    // Re-render when the home store changes
    M.on("home-store:changed", () => {
      document.querySelectorAll("[data-moonshot-tile]").forEach((t) => {
        const id = t.getAttribute("data-variant-id");
        const gid = toGid(id);
        const c = cache.get(gid);
        if (c) renderChip(t, c.data);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
